import { HyperElements, UnifiedCheckout, useHyper, useWidgets } from '@juspay-tech/react-hyper-js';
import { Box, Button, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import { LoadingText } from '@sdf/base';
import React, { useEffect, useState, useMemo } from 'react';
import { useHandleApiCall, useNotification } from '../../hooks';
import { API_CONSTANTS, PAYMENT_EXPIRY_TIME } from '../../utils';
import { unifiedCheckoutOptions } from '../../utils/hyperswitch/common';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import useHyperswitchLoader from '../../hooks/payment/useHyperswitchLoader';
import { Icon } from '@iconify/react';

const CountdownTimer = ({ handleCancel }: { handleCancel: () => void }) => {
  const [timeLeft, setTimeLeft] = useState<number>(PAYMENT_EXPIRY_TIME);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }

    if (timeLeft === 0) {
      handleCancel();
    }
  }, [timeLeft, handleCancel]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <Typography variant="body2" textAlign="center" color="#AAA" mt={1}>
      Only {formatTime(timeLeft)} left to claim this offer!
    </Typography>
  );
};

const CheckoutForm = ({
  handleCancel,
  paymentId,
  handleSuccess,
  amount,
  discount,
  interval,
  name,
}: {
  paymentId: string;
  amount: number;
  interval: string;
  discount: number;
  name: string;
  handleCancel: () => void;
  handleSuccess: () => void;
}) => {
  const { post } = useHandleApiCall();
  const [isLoading, setIsLoading] = useState(true);
  const discountedPrice = amount - amount * ((discount || 0) / 100);

  const theme = useTheme();

  // cancel payment
  const cancelPaymentMutation = post(
    API_CONSTANTS.CANCEL_PAYMENT.Key,
    `api/payments/${paymentId}/cancel`
  );

  const hyper = useHyper();
  const widgets = useWidgets();

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!hyper) {
      console.warn('Hyper instance is not loaded yet.');
      return;
    }

    if (!widgets) {
      console.warn('Widgets instance is not loaded yet.');
      return;
    }

    const paymentElement = widgets?.getElement('payment');

    if (paymentElement) {
      paymentElement.on('ready', () => {
        setIsLoading(false);
      });
    }

    //Look for a parameter called `payment_intent_client_secret` in the url which gives a payment ID, which is then used to retrieve the status of the payment
    const paymentID = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );

    if (!paymentID) {
      console.warn('No payment intent client secret found in the URL.');
      return;
    }

    hyper.retrievePaymentIntent(paymentID).then(({ paymentIntent }) => {
      console.log('Payment Intent Retrieved:');
      switch (paymentIntent?.status) {
        case 'succeeded':
          enqueueSnackbar('Payment succeeded!', { variant: 'success' });
          break;
        case 'processing':
          enqueueSnackbar('Your payment is processing.', { variant: 'info' });
          break;
        case 'requires_payment_method':
          enqueueSnackbar('Your payment was not successful, please try again.', {
            variant: 'error',
          });
          break;
        case 'failed':
          enqueueSnackbar('Your payment was not successful, please try again.', {
            variant: 'error',
          });
          break;
        default:
          enqueueSnackbar('Something went wrong.', {
            variant: 'error',
          });
          break;
      }
    });
  }, [hyper, widgets]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hyper || !widgets) {
      console.error('Hyper.js or Widgets are not loaded yet.');
      // hyper-js has not yet loaded.
      // Make sure to disable form submission until hyper-js has loaded.
      return;
    }
    console.log('Submitting payment...');
    setIsLoading(true);

    const response = await hyper.confirmPayment({
      widgets,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: `${window?.location?.origin}/dashboard`,
      },
      redirect: 'if_required',
    });

    // This point will only be reached if there is an immediate error occurring while confirming the payment. Otherwise, your customer will be redirected to your `return_url`

    // For some payment flows such as Sofort, iDEAL, your customer will be redirected to an intermediate page to complete authorization of the payment, and then redirected to the `return_url`.

    if (response) {
      if (response.status === 'succeeded') {
        handleSuccess();
      } else if (response?.error) {
        enqueueSnackbar(response?.error?.message, { variant: 'error' });
      } else if (response.status === 'failed') {
        enqueueSnackbar(response.error_message, { variant: 'error' });
      }
    } else {
      enqueueSnackbar('An unexpected error occurred.', { variant: 'error' });
    }
    setIsLoading(false);
  };

  const handleUserInitiateCancel = async () => {
    await cancelPaymentMutation.mutateAsync({
      cancellation_reason: 'requested_by_customer',
    });
    handleCancel();
  };

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        color="text.primary"
        padding={2}
        borderRadius={2}
      >
        <Typography variant="h4" fontWeight="bold" textAlign="center">
          Payment Details
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          padding={2}
          borderRadius={2}
          width="100%"
          maxWidth="300px"
          marginTop={1}
          bgcolor={(theme) => theme.palette.background?.ternaryV2}
        >
          <Typography variant="body2" component="span">
            {name}
          </Typography>
          {interval === 'Special' ? (
            <>
              <Typography variant="subtitle2" fontWeight="medium" textAlign="center" color="#BBB">
                Try it free for 7 days
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" textAlign="center" mt={0.5}>
                Then just ${amount}/year, cancel anytime
              </Typography>
            </>
          ) : (
            <Typography variant="h4" fontWeight="bold">
              ${discountedPrice.toFixed(2)}
              {interval === 'Monthly' && (
                <Typography variant="body2" component="span">
                  {' '}
                  /month
                </Typography>
              )}
              {interval === 'Yearly' && (
                <Typography variant="body2" component="span">
                  {' '}
                  /year
                </Typography>
              )}
            </Typography>
          )}
          <CountdownTimer handleCancel={handleCancel} />
        </Box>
      </Box>
      {/* <Divider
        sx={{
          borderColor: (theme) => theme.palette.background?.border ,
        }}
      /> */}
      <form id="payment-form" onSubmit={handleSubmit}>
        <Box style={{ padding: '8px 16px 4px 16px' }}>
          <UnifiedCheckout id="unified-checkout" options={unifiedCheckoutOptions(theme)} />

          {isLoading && (
            <Stack textAlign="center" py={0.5}>
              <LoadingText text="Please wait, processing payment..." sx={{ fontSize: 15 }} />
            </Stack>
          )}
        </Box>
        <Box display={'flex'} justifyContent={'space-between'} gap={1} padding={2}>
          <Button
            variant="outlined"
            sx={{ flex: 1, color: (theme) => theme.palette.text.primary, boxShadow: 'none' }}
            onClick={handleUserInitiateCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            id="submit"
            onClick={handleSubmit}
            sx={{
              flex: 1,
              boxShadow: 'none',
              color: theme.palette.text.primary,
              display: 'flex',
              flexDirection: 'row',
              whiteSpace: 'nowrap',
            }}
            loading={isLoading}
          >
            {interval === 'Special' ? 'Start Free Trial' : 'Pay Now'}
            <Icon
              icon="mdi:shield-lock-outline"
              width={'17'}
              height={'17'}
              style={{ marginLeft: '4px', flexShrink: 0 }}
            />
          </LoadingButton>
        </Box>
        {interval === 'Special' && (
          <Box display="flex" justifyContent="center" mt={0.5}>
            <Typography variant="caption" textAlign="center">
              You won’t be charged until your trial ends.
            </Typography>
          </Box>
        )}
      </form>
    </Box>
  );
};

interface CheckoutPageProps {
  options: {
    clientSecret: string;
    paymentId: string;
  } | null;
  amount: number;
  discount: number;
  interval: string;
  name: string;
  handleCancel: () => void;
  handleSuccess: () => void;
}

const CheckoutPage = ({
  options,
  handleSuccess,
  handleCancel,
  amount,
  discount,
  interval,
  name,
}: CheckoutPageProps) => {
  // Create a global hyperPromise using useMemo
  const { loadHyper } = useHyperswitchLoader();
  const hyperPromise = useMemo(
    () =>
      loadHyper(process.env.NEXT_PUBLIC_HYPERSWITCH_PUBLISH_KEY, {
        customBackendUrl: process.env.NEXT_PUBLIC_HYPERSWITCH_BACKEND_URL,
      }),
    [loadHyper]
  );

  return (
    <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
      <Paper
        elevation={5}
        sx={{
          width: '100%',
          bgcolor: (theme) => theme.palette.background?.primaryV2,
          opacity: 1,
          border: (theme) => `0.5px solid ${theme.palette.background?.ternaryV2}`,
        }}
      >
        {options && (
          <HyperElements options={options} hyper={hyperPromise}>
            <CheckoutForm
              handleCancel={handleCancel}
              handleSuccess={handleSuccess}
              paymentId={options.paymentId}
              amount={amount}
              discount={discount}
              interval={interval}
              name={name}
            />
          </HyperElements>
        )}
      </Paper>
    </Box>
  );
};

export default React.memo(CheckoutPage);
